import { readSessionToken, saveSessionToken } from 'shared/utils/session-token';
import { GlobalDispatch } from 'context';
import { BranchOffices } from 'domain/branch-offices';
import { Login } from 'domain/login';
import { GlobalAction } from 'store/global-actions';
import { RequestAutentication } from '../proxy/request-autentication';

const { StartFetching, FinishedFetching } = GlobalAction;

const ORIGIN_SESSION_KEY = 'pharmaSoftImpersonationOrigin';
const LOG_ID_KEY = 'pharmaSoftImpersonationLogId';

interface StashedSession {
  token: string;
  login: Login;
  branchOfficeSelected: BranchOffices | null;
}

export const forgetImpersonation = (): void => {
  sessionStorage.removeItem(ORIGIN_SESSION_KEY);
  sessionStorage.removeItem(LOG_ID_KEY);
};

const startImpersonation = async (
  dispatch: GlobalDispatch,
  userId: number,
  currentLogin: Login,
  currentBranchOfficeSelected: BranchOffices | null,
): Promise<void> => {
  dispatch({ type: StartFetching });
  try {
    const currentToken = readSessionToken() ?? '';
    const { login: impersonatedLogin, impersonationLogId } = await RequestAutentication.startImpersonation(userId);

    const stashed: StashedSession = {
      token: currentToken,
      login: currentLogin,
      branchOfficeSelected: currentBranchOfficeSelected,
    };
    sessionStorage.setItem(ORIGIN_SESSION_KEY, JSON.stringify(stashed));
    sessionStorage.setItem(LOG_ID_KEY, String(impersonationLogId));
    saveSessionToken(impersonatedLogin.token);

    dispatch({ type: GlobalAction.LoginInfo, payload: impersonatedLogin });
    dispatch({
      type: GlobalAction.ImpersonationStarted,
      payload: `${impersonatedLogin.firstName} ${impersonatedLogin.lastName} — ${impersonatedLogin.company.name}`,
    });
  } finally {
    dispatch({ type: FinishedFetching });
  }
};

const stopImpersonation = async (dispatch: GlobalDispatch): Promise<void> => {
  const originRaw = sessionStorage.getItem(ORIGIN_SESSION_KEY);
  if (!originRaw) return;

  const logId = sessionStorage.getItem(LOG_ID_KEY);
  const origin = JSON.parse(originRaw) as StashedSession;

  dispatch({ type: StartFetching });
  try {
    if (logId) {
      await RequestAutentication.stopImpersonation(Number(logId));
    }

    saveSessionToken(origin.token);
    forgetImpersonation();

    dispatch({ type: GlobalAction.LoginInfo, payload: origin.login });
    dispatch({ type: GlobalAction.BranchOfficeSelected, payload: origin.branchOfficeSelected });
    dispatch({ type: GlobalAction.ImpersonationEnded });

    const accessUser = await RequestAutentication.getAccessModuleUser();
    dispatch({ type: GlobalAction.AccessUser, payload: accessUser });
  } finally {
    dispatch({ type: FinishedFetching });
  }
};

export const ImpersonationApiActions = {
  startImpersonation,
  stopImpersonation,
};
